import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { projectsAPI } from '../../services/api';

const EditProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // API calls are now handled through the centralized API service

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError('');
      
      try {
        const response = await projectsAPI.getProject(id);
        
        if (response.data.status === 200) {
          setProject(response.data.data.project);
        } else {
          setError(response.data.message || 'Failed to load project');
          navigate('/projects');
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        
        if (err.response && err.response.status === 404) {
          setError('Project not found');
          navigate('/projects');
        } else {
          setError('Failed to load project. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchProject();
  }, [id, navigate]);

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Project name is required')
      .max(100, 'Project name cannot exceed 100 characters'),
    description: Yup.string()
      .max(500, 'Description cannot exceed 500 characters')
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setError('');
    
    try {
      const response = await projectsAPI.updateProject(id, {
        name: values.name,
        description: values.description
      });
      
      if (response.data.status === 200) {
        // Project updated successfully, redirect to project page
        navigate(`/projects/${id}`);
      } else {
        setError(response.data.message || 'Failed to update project');
      }
    } catch (err) {
      console.error('Error updating project:', err);
      
      if (err.response) {
        setError(err.response.data.message || 'Failed to update project');
      } else {
        setError('Network error. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <h3>Loading project...</h3>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container>
        <Alert variant="danger">
          Project not found or you don't have access to it.
        </Alert>
        <Button variant="primary" onClick={() => navigate('/projects')}>
          Back to Projects
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Edit Project</h1>
          
          {error && <Alert variant="danger">{error}</Alert>}
        </Col>
      </Row>
      
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <Formik
                initialValues={{
                  name: project.name,
                  description: project.description
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ 
                  handleSubmit, 
                  handleChange, 
                  handleBlur, 
                  values, 
                  touched, 
                  errors, 
                  isSubmitting 
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.name && !!errors.name}
                        placeholder="Enter project name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.description && !!errors.description}
                        placeholder="Enter project description"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="secondary" 
                        onClick={() => navigate(`/projects/${id}`)}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to Project
                      </Button>
                      
                      <Button 
                        type="submit" 
                        variant="primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Project Details</h5>
            </Card.Header>
            <Card.Body>
              <p><strong>Created:</strong> {new Date(project.created_at).toLocaleDateString()}</p>
              {project.updated_at && (
                <p><strong>Last Updated:</strong> {new Date(project.updated_at).toLocaleDateString()}</p>
              )}
              <p><strong>Documents:</strong> {project.document_count || 0}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EditProject;
